/**
 * Talking to CherryClipz from the phone: signing in, sending a video for the AI pass, and
 * reading the finished transcript back.
 *
 * These are the SAME endpoints the website uses, in the same order — a phone upload is not a
 * special case, it is the ordinary upload path performed from a different screen. Inside the
 * shell every request goes through Capacitor's native HTTP (see `call` below), so the WebView's
 * https://localhost origin never has to appear in the backend's CORS allowlist.
 */
import {
  authUrl, csrfUrl, getSentencesUrl, getUserUrl, presignedUrlEndpoint,
  updateUploadVideoData, getUserTranscriptVideosUrl, versionUrl,
} from "../constants";

const TOKEN_KEY = "token";   // the same key the website uses, so the two agree

/** Recognised by the UI, which turns it into a "sign in again" button rather than a dead end. */
export const SIGNED_OUT = "SIGNED_OUT";
/** The account's plan will not allow this upload (free hours used up) — offer the upgrade. */
export const PLAN_LIMIT = "PLAN_LIMIT";

/**
 * A breadcrumb from the last sign-in attempt, kept on the phone.
 *
 * An error shown on the sign-in screen is gone the moment you navigate away, so a failure that
 * happens at the code step becomes invisible a second later — which is precisely the failure we
 * have been unable to see. This survives, and "Check my connection" prints it.
 */
const LAST_KEY = "cc_last_signin";

export function noteSignIn(entry) {
  try {
    window.localStorage.setItem(LAST_KEY, JSON.stringify({ at: new Date().toISOString(), ...entry }));
  } catch (e) { /* ignore */ }
}

export function lastSignIn() {
  try { return JSON.parse(window.localStorage.getItem(LAST_KEY) || "null"); }
  catch (e) { return null; }
}

/* ── one network stack ───────────────────────────────────────────────── */

/**
 * Every request to CherryClipz goes through the SAME stack, on purpose.
 *
 * Capacitor's patched `fetch` splits traffic: POSTs go out through native Android HTTP, GETs
 * through the WebView. Those two carry different User-Agent strings. The backend fingerprints a
 * session as (User-Agent + IP) at sign-in and DESTROYS the session on the first request that
 * does not match — so sign-in (a POST, native agent) followed by get-user (a GET, WebView agent)
 * signed the phone out within a second, in every build, while the phone's browser was fine.
 * Reproduced 2026-09-06 with curl against the dev backend: the first GET answers
 * `device_mismatch`, every request after that "Authentication required".
 *
 * Calling the native plugin directly for ALL methods means sign-in and everything after it look
 * like one device. Nothing on the server changes. On the web this is plain `fetch`.
 */
const nativeBridge = () => {
  if (typeof window === "undefined") return null;
  const cap = window.Capacitor;
  try {
    if (!cap || typeof cap.isNativePlatform !== "function" || !cap.isNativePlatform()) return null;
  } catch (e) {
    return null;
  }
  return typeof cap.nativePromise === "function" ? cap : null;
};

/**
 * Read a Blob as base64 (no data: prefix).
 *
 * Needed because the native HTTP layer takes a request body as a STRING. Binary has to reach it
 * base64-encoded and be marked dataType "file", which is the one path in
 * CapacitorHttpUrlConnection that writes the exact bytes (Base64.getDecoder().decode) instead of
 * writing a string.
 */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("Could not read the video off this phone."));
    r.onload = () => {
      const out = String(r.result || "");
      const comma = out.indexOf(",");
      resolve(comma >= 0 ? out.slice(comma + 1) : out);
    };
    r.readAsDataURL(blob);
  });
}

/**
 * PUT a video to S3 with its bytes intact.
 *
 * THE BUG THIS EXISTS FOR: enabling CapacitorHttp patches window.fetch so every non-GET goes
 * through native Android HTTP — including this upload. Capacitor's patch reads the body stream
 * and runs it through `new TextDecoder().decode(...)`, which is lossy for anything that is not
 * UTF-8 text. A video became mojibake, S3 stored that, and AssemblyAI answered every single
 * phone upload with "Transcoding failed. File type application/json ... may be unsupported".
 * Every upload from the app failed this way; website uploads were fine because the browser's
 * own fetch was never patched. Confirmed 2026-09-06 against the AssemblyAI API: four jobs, all
 * errored, all from the phone, while the website's jobs two days earlier completed.
 *
 * So the bytes go as base64 with dataType "file". Slower and heavier than a streamed upload —
 * the whole file passes through memory as a ~1.33x string — but it is the only native path that
 * preserves them, and native is required because the WebView's https://localhost origin is not
 * in the bucket's CORS allowlist (and must not be added).
 */
async function putVideo(url, blob) {
  const type = blob.type || "video/mp4";
  const cap = nativeBridge();
  if (!cap) {
    return fetch(url, { method: "PUT", body: blob, headers: { "Content-Type": type } });
  }
  const res = await cap.nativePromise("CapacitorHttp", "request", {
    url,
    method: "PUT",
    headers: { "Content-Type": type },
    data: await blobToBase64(blob),
    dataType: "file",
  });
  const status = Number(res && res.status) || 0;
  return { ok: status >= 200 && status < 300, status };
}

async function call(url, options = {}) {
  const cap = nativeBridge();
  if (!cap) return fetch(url, options);

  const { method = "GET", headers = {}, body } = options;
  const req = { url, method, headers };
  const contentType = headers["Content-Type"] || headers["content-type"] || "";
  if (body instanceof URLSearchParams) {
    req.data = body.toString();
    req.dataType = "text";
  } else if (typeof body === "string") {
    if (contentType.includes("application/json")) {
      try { req.data = JSON.parse(body); } catch (e) { req.data = body; }
      req.dataType = "json";
    } else {
      req.data = body;
      req.dataType = "text";
    }
  } else if (body && typeof body === "object") {
    req.data = body;
    req.dataType = "json";
  }

  const res = await cap.nativePromise("CapacitorHttp", "request", req);
  const status = Number(res && res.status) || 0;
  const raw = res ? res.data : "";
  return {
    ok: status >= 200 && status < 300,
    status,
    json: async () => (typeof raw === "string" ? JSON.parse(raw) : raw),
    text: async () => (typeof raw === "string" ? raw : JSON.stringify(raw)),
  };
}

export const readToken = () => {
  try { return JSON.parse(window.localStorage.getItem(TOKEN_KEY) || "null"); }
  catch (e) { return null; }
};
export const writeToken = (t) => {
  try { window.localStorage.setItem(TOKEN_KEY, JSON.stringify(t)); } catch (e) { /* ignore */ }
};
export const forgetToken = () => {
  try { window.localStorage.removeItem(TOKEN_KEY); } catch (e) { /* ignore */ }
};
export const isSignedIn = () => !!readToken();

const form = (obj) => {
  const p = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => p.append(k, v == null ? "" : String(v)));
  return p;
};

/** Django wants a CSRF token on state-changing posts; it is handed out unauthenticated. */
async function csrf() {
  try {
    const r = await call(csrfUrl, { credentials: "include" });
    const j = await r.json();
    return j.csrfToken || "";
  } catch (e) {
    return "";
  }
}

const authed = () => {
  const t = readToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/* ── signing in ──────────────────────────────────────────────────────── */

/** Step one: ask CherryClipz to email a code. */
export async function sendCode(email) {
  const r = await call(authUrl.get_email, {
    method: "POST",
    body: form({ email }),
    headers: { "Content-Type": "application/x-www-form-urlencoded", "X-CSRFToken": await csrf() },
  });
  const body = await r.text().catch(() => "");
  noteSignIn({ step: "code requested", status: r.status, reply: body.slice(0, 120) });
  if (!r.ok) {
    let j = {};
    try { j = JSON.parse(body); } catch (e) { /* not json */ }
    throw new Error(j.message || j.error || `That email was not accepted (${r.status}).`);
  }
  return true;
}

/** Step two: swap the code for a token. */
export async function verifyCode(email, code) {
  const r = await call(authUrl.get_pass, {
    method: "POST",
    body: form({ email, code }),
    headers: { "Content-Type": "application/x-www-form-urlencoded", "X-CSRFToken": await csrf() },
  });
  const body = await r.text().catch(() => "");
  let j = {};
  try { j = JSON.parse(body); } catch (e) { /* not json */ }
  noteSignIn({
    step: "code entered",
    status: r.status,
    gotToken: !!j.token,
    reply: (j.message || j.error || body || "").toString().slice(0, 120),
  });
  if (!r.ok || !j.token) throw new Error(j.message || j.error || `That code did not work (${r.status}).`);
  writeToken(j.token);
  noteSignIn({ step: "signed in", status: r.status, gotToken: true, saved: readToken() === j.token });
  // Check it actually stuck. Storage can be full or blocked, and a sign-in that silently kept
  // nothing is worse than one that failed — you find out later, holding no session.
  if (readToken() !== j.token) throw new Error("This phone would not save the sign-in.");
  return j.token;
}

/**
 * Who is signed in, or null.
 *
 * Deliberately NEVER deletes the token. An earlier version threw it away on any 401, which
 * meant one unlucky call right after signing in left the app *looking* signed in while holding
 * nothing — and the next upload went out with no Authorization header at all, which the backend
 * answers with "Invalid token". Reporting and destroying are different jobs; this one reports.
 */
export async function whoAmI() {
  if (!isSignedIn()) return null;
  try {
    const r = await call(getUserUrl, { headers: authed() });
    if (!r.ok) return null;
    const j = await r.json();
    return j.user || j;
  } catch (e) {
    return null;
  }
}

/** Is the stored token still a live session? The only thing allowed to conclude "signed out". */
export async function tokenStillGood() {
  if (!isSignedIn()) return false;
  try {
    const r = await call(getUserUrl, { headers: authed() });
    if (r.status === 401 || r.status === 403) return false;
    return r.ok;
  } catch (e) {
    return true;   // offline is not the same as signed out
  }
}

/* ── sending a video for the AI pass ─────────────────────────────────── */

const uuid = () =>
  (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `x${Date.now()}${Math.random().toString(16).slice(2)}`;

/**
 * Upload one video and register it, which is what starts transcription server-side.
 *
 * The file is read back out of app storage through its playable URL — the same URL the video
 * element uses — because that is the only handle the web layer has on a native file. For a
 * phone-sized video this is a local read, not a network one.
 *
 * @returns {Promise<{downloadId:number}>}
 */
/** The language picker's options; `langs` is what the backend receives ([] = auto). */
export const LANGUAGE_CHOICES = [
  { id: "auto", langs: [] },
  { id: "en", langs: ["en"] },
  { id: "he", langs: ["he"] },
  { id: "yi", langs: ["yi"] },
  { id: "mix", langs: ["en", "he", "yi"] },
];
const LANG_KEY = "cc_mobile_languages";
export const readLanguages = () => {
  try { const v = JSON.parse(window.localStorage.getItem(LANG_KEY) || "[]"); return Array.isArray(v) ? v : []; }
  catch (e) { return []; }
};
export const writeLanguages = (langs) => {
  try { window.localStorage.setItem(LANG_KEY, JSON.stringify(langs || [])); } catch (e) { /* ignore */ }
};

export async function sendForAi(source, onProgress = () => {}, languages = []) {
  // Fail here, clearly, rather than sending a request with no Authorization header and letting
  // the server answer with something that sounds like a bad token rather than a missing one.
  if (!isSignedIn()) throw new Error(SIGNED_OUT);
  const name = source.name || "video.mp4";
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : ".mp4";
  const id = uuid();

  onProgress({ phase: "reading", pct: 0 });
  const blob = await (await fetch(source.url)).blob();

  onProgress({ phase: "asking", pct: 0 });
  // No `credentials: include`: the backend authenticates this from the Bearer header, and
  // letting a stale session cookie ride along only gives Django a second, contradictory idea
  // of who is calling.
  const presign = await call(presignedUrlEndpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      "X-CSRFToken": await csrf(),
      ...authed(),
    },
    body: form({
      filename: `${id}${ext}`,
      title: name,
      size: blob.size,
      duration_seconds: Math.round(source.duration || 0),
      uuid: id,
      original_filename: name,
    }),
  });
  if (presign.status === 401) throw new Error(SIGNED_OUT);
  if (presign.status === 403) throw new Error(PLAN_LIMIT);
  if (!presign.ok) {
    const j = await presign.json().catch(() => ({}));
    throw new Error(j.error || `CherryClipz would not accept the upload (${presign.status}).`);
  }
  const { url, object_key: objectKey } = await presign.json();
  if (!url) throw new Error("CherryClipz did not return a place to put the video.");

  onProgress({ phase: "sending", pct: 0 });
  const put = await putVideo(url, blob);
  if (!put.ok) throw new Error(`The upload did not finish (${put.status || "no answer"}).`);
  onProgress({ phase: "sending", pct: 100 });

  const reg = await call(updateUploadVideoData, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRFToken": await csrf(), ...authed() },
    body: JSON.stringify({
      object_key: objectKey,
      title: name,
      size: blob.size,
      duration_seconds: Math.round(source.duration || 0),
      uuid: id,
      original_filename: name,
      // [] = auto-detect among English / Hebrew / Yiddish, keeping mid-sentence switches
      languages: Array.isArray(languages) ? languages : [],
    }),
  });
  if (!reg.ok) {
    const j = await reg.json().catch(() => ({}));
    throw new Error(j.message || j.error || "CherryClipz could not register the video.");
  }
  const body = await reg.json().catch(() => ({}));
  onProgress({ phase: "listening", pct: 0 });
  return { downloadId: body.id || body.download_id || null, uuid: id, objectKey, title: name };
}

/* ── waiting, then reading the transcript back ───────────────────────── */

const baseName = (k) => String(k || "").split("/").pop();

/**
 * Wait until CherryClipz has transcribed this upload, then hand back the transcript id.
 *
 * Polls `get-user-transcript-videos/` — the same list the website's library reads — which shows
 * ONE row per video: while the worker is on it, a pending row `{ id, title, task_status }`; when
 * it is done, the finished row `{ id: <transcript id>, title, video_path, … }`. The finished row
 * is recognised by its S3 key (`video_path`), which the presign step handed us — the one thing
 * that is unique per upload and needs no server change. (The earlier version polled the
 * finished-only `user-downloaded-videos/` list for a `uuid` field that list never carried, so
 * it could not recognise its own video and ran out the clock.)
 *
 * Being offline for a while is not a failure: a phone sleeps, changes network, loses signal.
 * A request that cannot even reach CherryClipz is simply tried again. Only a definite 401 means
 * signed out, and only "the pending row vanished and nothing finished replaced it" means the
 * transcription failed.
 */
export async function waitForTranscript(
  { objectKey, title } = {},
  { timeoutMs = 20 * 60 * 1000, onTick = () => {}, pollMs = null, missingPolls = 8 } = {},
) {
  const started = Date.now();
  const wantKey = String(objectKey || "");
  const wantBase = baseName(wantKey);
  let wait = pollMs == null ? 4000 : pollMs;
  let seenPending = false;
  let goneFor = 0;

  while (Date.now() - started < timeoutMs) {
    let r = null;
    try {
      r = await call(getUserTranscriptVideosUrl, { headers: authed() });
    } catch (e) {
      r = null;   // no signal right now; keep waiting
    }
    if (r && (r.status === 401 || r.status === 403)) throw new Error(SIGNED_OUT);
    if (r && r.ok) {
      const list = await r.json().catch(() => []);
      const rows = Array.isArray(list) ? list : (list.results || list.videos || []);

      const done = rows.find((v) => v && v.video_path && (
        String(v.video_path) === wantKey || baseName(v.video_path) === wantBase
      ));
      if (done) return { transcriptId: done.id };

      const pending = rows.find((v) => v && v.task_status && !v.video_path && (!title || v.title === title));
      if (pending) {
        seenPending = true;
        goneFor = 0;
        const status = String(pending.task_status || "");
        onTick(status);
        if (status.toUpperCase().includes("FAIL")) throw new Error("CherryClipz could not transcribe that video.");
      } else if (seenPending) {
        goneFor += 1;
        if (goneFor >= missingPolls) throw new Error("CherryClipz could not transcribe that video.");
      }
    }
    await new Promise((res) => setTimeout(res, wait));
    if (pollMs == null) wait = Math.min(15000, Math.round(wait * 1.25));   // back off; a long video takes minutes
  }
  throw new Error("That is taking longer than expected. It will be in your library when it is done.");
}

/* ── your library ────────────────────────────────────────────────────── */

/**
 * Every video in this account's library that has a transcript, newest first.
 *
 * Reads the same list the website's My Library reads. A finished row carries a signed link to
 * the video (good for 24h) and a poster; a row still being transcribed has neither and is left
 * out — the phone opens finished work. Nothing here re-uploads or re-transcribes anything.
 */
export async function listLibrary() {
  const r = await call(getUserTranscriptVideosUrl, { headers: authed() });
  if (r.status === 401 || r.status === 403) throw new Error(SIGNED_OUT);
  if (!r.ok) throw new Error("Could not read your library.");
  const j = await r.json().catch(() => []);
  const rows = Array.isArray(j) ? j : (j.results || j.videos || []);
  return rows
    .filter((v) => v && v.video_path && v.id != null)
    .map((v) => ({
      transcriptId: v.id,
      // An upload with no picture (an mp3/m4a/wav sent from the website) plays as audio.
      audioOnly: /\.(m4a|mp3|wav|aac|ogg|opus|flac|aiff?)$/i.test(String(v.video_path || "")),
      title: String(v.title || "Untitled"),
      createdAt: String(v.created_at || ""),
      clipsCount: Number(v.clips_count) || 0,
      duration: Number(v.duration_in_seconds) || 0,
      video: String(v.video || ""),
      thumbnail: String(v.thumbnail || ""),
    }));
}

/**
 * Pull the sentence rows out of page 0 of `user-create/<id>/`.
 *
 * `Sentence.to_list` does not return rows. It returns a BUNDLE:
 *   [ video_url, [ ...rows ], title, title_slug, video_duration, ext ]
 * and the view puts that bundle under the key "sentences". Read literally, "sentences" is a
 * URL, a list, three strings and a number — so treating it as rows yields nothing with a
 * start_in, and the phone reported "No speech found" on a transcript the website was showing
 * with 19 clips. Find the list of objects wherever it sits, and accept the plainer shapes too.
 */
function sentenceRows(j) {
  const isRow = (x) => x && typeof x === "object" && !Array.isArray(x);
  const pick = (arr) => {
    if (!Array.isArray(arr)) return null;
    if (arr.length && arr.every((x) => isRow(x) || typeof x === "string")) return arr;
    const inner = arr.find((x) => Array.isArray(x) && x.length && isRow(x[0]));
    return inner || null;
  };
  return pick(j) || pick(j && j.sentences) || [];
}

/**
 * Read a finished transcript and hand back sentences in the shape the phone already uses,
 * carrying the things only the cloud knows: who spoke, and how the line feels.
 *
 * Reads `user-create/<id>/0/`, the paged endpoint whose page 0 is `{ sentences: [...] }` — a
 * single JSON object. NOT the streaming endpoint (`user-create-stream/`): that one sends
 * newline-delimited JSON with a Content-Type of application/json, and the native HTTP layer
 * parses any application/json body with `new JSObject(text)`, which reads the FIRST object and
 * silently drops every line after it. The app received the stream's header, zero sentences,
 * and told Isaac "No speech found" about a video AssemblyAI had just transcribed 90 words of.
 */
export async function fetchSentences(transcriptId) {
  const r = await call(`${getSentencesUrl}${transcriptId}/0/`, { headers: authed() });
  if (r.status === 401 || r.status === 403) throw new Error(SIGNED_OUT);
  if (!r.ok) throw new Error("Could not read that transcript.");
  const j = await r.json().catch(() => null);
  const rows = sentenceRows(j);

  // The backend's own integer code for sentiment; the website maps it the same way.
  const SENTIMENT = { 0: "positive", 1: "negative", 2: "neutral" };

  const out = [];
  rows.forEach((raw) => {
    let d = raw;
    if (typeof d === "string") { try { d = JSON.parse(d); } catch (e) { return; } }
    if (!d || typeof d !== "object") return;
    const text = String(d.sentence || "").trim();
    if (!text) return;                        // a silence row is not a spoken clip
    const start = Number(d.start_in);
    const end = Number(d.end_in);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return;
    out.push({
      text,
      start,                                   // seconds, same axis the phone already uses
      end,
      thumbnail: d.thumbnail ? String(d.thumbnail) : "",   // the server's own poster, signed
      // per-word timings ([start, end] each, in word order) — what lights the words up as spoken
      words: Array.isArray(d.utterance)
        ? d.utterance.filter((w) => Array.isArray(w) && w.length >= 2).map((w) => ({ start: Number(w[0]), end: Number(w[1]) }))
        : [],
      speaker: d.speaker ? String(d.speaker).trim() : "",
      sentiment: SENTIMENT[d.sentiment] || (typeof d.sentiment === "string" && d.sentiment ? d.sentiment : "neutral"),
    });
  });
  return out.sort((a, b) => a.start - b.start);
}


/* ── which build is this, and is there a newer one ───────────────────── */

/**
 * This app's own build number, baked in by mobile/name-build.sh from mobile/app-version.json
 * (REACT_APP_CC_APP_BUILD / REACT_APP_CC_APP_VERSION). On the website, and in any bundle made
 * without the script, it is unknown — and an unknown build is never told to update, because
 * nobody can say whether it is old.
 */
export function appBuild() {
  const code = parseInt(process.env.REACT_APP_CC_APP_BUILD || "", 10);
  return {
    code: Number.isFinite(code) ? code : null,
    name: process.env.REACT_APP_CC_APP_VERSION || "",
  };
}

/** What the server says about itself and about the newest Android build; null when unreachable. */
export async function serverVersion() {
  try {
    const r = await call(versionUrl);
    if (!r.ok) return null;
    return await r.json();
  } catch (e) {
    return null;
  }
}

/**
 * Is there a newer app than this one? Build numbers are compared as integers, never as version
 * strings. `available` is false whenever EITHER side is unknown, so a phone with no signal, or a
 * build made outside the script, is never nagged by mistake.
 */
export async function checkForUpdate() {
  const current = appBuild();
  const v = await serverVersion();
  const code = v && v.android ? Number(v.android.code) : NaN;
  const latest = Number.isFinite(code) ? { code, name: String(v.android.name || "") } : null;
  const available = !!(latest && current.code != null && latest.code > current.code);
  return { available, current, latest, backend: v && v.backend ? v.backend : null };
}

/**
 * A one-tap self-check, so "it says I'm not signed in" stops being a guessing game.
 *
 * Reports what is actually true on THIS phone, in this build: can it reach CherryClipz, is a
 * token stored, and does the server accept it. Each answer is a fact rather than an inference,
 * which is the difference between fixing this and speculating about it again.
 */
export async function diagnose() {
  const out = { backend: "?", token: "none", accepted: "?", who: "", detail: "", last: "", server: "", app: "", verdict: "offline" };

  const l = lastSignIn();
  if (l) {
    out.last = [l.step, l.status ? `status ${l.status}` : "", l.gotToken === false ? "no token returned" : "",
                l.saved === false ? "NOT saved" : "", l.reply ? `"${l.reply}"` : ""]
      .filter(Boolean).join(" · ");
  }

  try {
    const r = await call(csrfUrl, { credentials: "include" });
    out.backend = r.ok ? "reachable" : `error ${r.status}`;
  } catch (e) {
    out.backend = "no connection";
    out.detail = e.message || "";
  }

  // Which build answered, and which build this is — so "is the fix deployed?" and "is his phone
  // on the new one?" are read off the screen instead of guessed.
  const v = await serverVersion();
  if (v && v.backend) {
    out.server = [v.backend.commit, v.backend.branch].filter((x) => x && x !== "unknown").join(" · ") || "unknown";
  }
  const me = appBuild();
  out.app = me.code != null ? `build ${me.code}${me.name ? ` (${me.name})` : ""}` : "build unknown";

  const t = readToken();
  if (!t) {
    out.token = "none stored";
    // Reachable and simply not signed in is a normal state, not a fault.
    out.verdict = out.backend === "reachable" ? "signed_out" : "offline";
    return out;
  }
  // Never show the whole session key; enough to tell two apart.
  out.token = `stored (…${String(t).slice(-6)})`;

  try {
    const r = await call(getUserUrl, { headers: authed() });
    if (r.ok) {
      const j = await r.json().catch(() => ({}));
      const u = j.user || j;
      out.accepted = "yes";
      out.who = (u && (u.email || u.username)) || "";
      out.verdict = "ok";
    } else {
      out.accepted = `no (${r.status})`;
      out.detail = (await r.text().catch(() => "")).slice(0, 90);
      // The token is stored but the server will not take it — signing in again is the fix.
      out.verdict = "rejected";
    }
  } catch (e) {
    out.accepted = "could not ask";
    out.detail = e.message || "";
    out.verdict = "offline";
  }
  return out;
}
